/**
 * Main shell: landscape side navigation rail (avatar → account drawer, Learn, Playground,
 * Journal, My Studio, sparks) with FTUE / bonus-feature locks, plus the routed tab content.
 */
import { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { tabLock, unopenedLetters, type MainTab } from '@sketchling/core';
import { useAccess, useActiveProfile, useApp, useJournal, useProgress } from '../state/store';
import { useT } from '../lib/i18n';
import { play } from '../lib/sfx';
import { Avatar } from './characters';
import { AccountDrawer } from './AccountDrawer';
import { Icon, SparkGlyph, type IconName } from './icons';
import { toast } from './ui';
import { CollectRailItem } from './collect/CollectRailItem';

const TABS: { tab: MainTab; to: string; icon: IconName; label: 'nav.learn' | 'nav.playground' | 'nav.journal' | 'nav.studio' }[] = [
  { tab: 'learn', to: '/learn', icon: 'learn', label: 'nav.learn' },
  { tab: 'playground', to: '/playground', icon: 'playground', label: 'nav.playground' },
  { tab: 'journal', to: '/journal', icon: 'journal', label: 'nav.journal' },
  { tab: 'studio', to: '/studio', icon: 'studio', label: 'nav.studio' },
];

export function AppShell() {
  const t = useT();
  const nav = useNavigate();
  const loc = useLocation();
  const profile = useActiveProfile();
  const progress = useProgress();
  const access = useAccess();
  const enableStudio = useApp((s) => s.config.enableStudio);
  const journal = useJournal();
  const [drawer, setDrawer] = useState(false);
  const unread = journal.reduce((n, j) => n + j.comments.filter((c) => !c.seen).length, 0) + unopenedLetters(progress.mailing);

  return (
    <div className="shell bg-night">
      <nav className="rail" aria-label="Main">
        <button
          type="button"
          className="rail-avatar"
          aria-label="Account"
          data-testid="rail-avatar"
          onClick={() => {
            play('drawer');
            setDrawer(true);
          }}
        >
          {profile ? <Avatar avatar={profile.avatar} character={profile.character} size={46} /> : <Icon name="menu" />}
        </button>
        {TABS.map((tb) => {
          const lock = tabLock(progress, tb.tab, access, { enableStudio });
          if (tb.tab === 'studio' && lock.reason === 'disabled') return null;
          const active = loc.pathname.startsWith(tb.to);
          return (
            <NavLink
              key={tb.tab}
              to={tb.to}
              className="rail-item"
              aria-current={active ? 'page' : undefined}
              data-locked={lock.locked}
              data-testid={`tab-${tb.tab}`}
              onClick={(e) => {
                if (lock.locked) {
                  e.preventDefault();
                  play('disabled');
                  toast(lock.reason === 'drawTwo' ? t('nav.drawTwo') : t('nav.collectSparks', { n: lock.sparks ?? 0 }));
                  return;
                }
                play('click');
              }}
            >
              <Icon name={tb.icon} />
              <span>{t(tb.label)}</span>
              {lock.locked && <Icon name="lock" className="lock-badge" />}
              {tb.tab === 'journal' && !lock.locked && unread > 0 && <span className="badge-dot">{unread}</span>}
            </NavLink>
          );
        })}
        <CollectRailItem />
        <div className="rail-spacer" />
        <button type="button" className="rail-item" onClick={() => nav('/sparks')} aria-current={loc.pathname.startsWith('/sparks') ? 'page' : undefined} data-testid="tab-sparks">
          <SparkGlyph size={26} />
          <span>{progress.totalSparks}</span>
        </button>
      </nav>
      <main className="main">
        <Outlet />
      </main>
      {drawer && <AccountDrawer onClose={() => setDrawer(false)} />}
    </div>
  );
}
